import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AdminAuthStateService } from './auth.service';
import {
  AuthSessionRedirectService,
  isPublicAuthApiPath,
  shouldRedirectOnAuthFailure,
} from './auth-session-redirect.service';
import { environment } from 'src/environments/environment';

@Injectable()
export class AdminTokenInterceptor implements HttpInterceptor {
  constructor(
    private readonly authState: AdminAuthStateService,
    private readonly sessionRedirect: AuthSessionRedirectService,
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isAppApiRequest(req.url)) {
      return next.handle(req);
    }

    const token = this.authState.getToken();
    const skipAuthz = isPublicAuthApiPath(req.url);
    const tenantId = this.getTenantFromSubdomain() ?? this.authState.getTenant()?.id?.toString();

    const headers: Record<string, string> = {
      'X-Custom-Lang': this.getRequestLanguage(),
      'X-Request-ID': this.generateRequestId(),
      'X-Environment': String(environment.envName),
    };
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }
    if (token && !skipAuthz) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const handle = next.handle(req.clone({ setHeaders: headers }));

    if (skipAuthz) {
      return handle;
    }

    return handle.pipe(
      map((event) => {
        if (event instanceof HttpResponse && shouldRedirectOnAuthFailure(req.url, event.body)) {
          this.sessionRedirect.redirectToLogin();
          throw new HttpErrorResponse({
            error: event.body,
            status: 401,
            statusText: 'Unauthorized',
            url: event.url ?? req.url,
          });
        }
        return event;
      }),
      catchError((error: HttpErrorResponse) => {
        if (error instanceof HttpErrorResponse && shouldRedirectOnAuthFailure(req.url, error)) {
          this.sessionRedirect.redirectToLogin();
        }
        return throwError(() => error);
      }),
    );
  }

  private isAppApiRequest(url: string): boolean {
    const base = environment.apiBaseUrl.replace(/\/$/, '');
    const path = url.split('?')[0];
    return path.startsWith(base) || path.includes('/api/v1/');
  }

  private getTenantFromSubdomain(): string | null {
    if (globalThis.window === undefined) return null;
    const host = globalThis.location.hostname;
    const parts = host.split('.');
    if (parts.length < 2) return null;
    const subdomain = parts[0]?.trim();
    if (!subdomain || subdomain === 'localhost' || subdomain === 'www') return null;
    return subdomain;
  }

  private getRequestLanguage(): string {
    if (globalThis.window === undefined) return 'en';
    const stored = localStorage.getItem('i18n_locale')?.trim();
    if (stored) return stored;
    return typeof navigator !== 'undefined' && navigator.language
      ? navigator.language.split('-')[0]
      : 'en';
  }

  private generateRequestId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
